import React from "react";
import {
  Grid,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Storefront as StorefrontIcon,
  RoomService as RoomServiceIcon,
  Fastfood as FastfoodIcon,
} from "@mui/icons-material";
import { StandardCard } from "../../../../components/Cards";
import { CustomChip } from "../../../../components/CustomChip";
import { TopPerformersSkeleton } from "../skeletons";
import {
  IUpsellingDataItem,
  IServiceCategoryCard,
  IVenueCard,
  isServiceCategoryCard,
  isVenueCard,
  isFoodCard,
  IFoodCard,
} from "../../types/upsell.types";
import { IUpsellMetrics } from "../../hooks/useUpsellMetrics";
import { NoRecordFound } from "../../../../components/NoRecordFound/NoRecordFound";

interface TopPerformersProps {
  dashboardData: IUpsellingDataItem[] | null;
  metrics: IUpsellMetrics | null;
}

export const TopPerformers: React.FC<TopPerformersProps> = ({
  dashboardData,
}) => {
  if (!dashboardData || dashboardData.length === 0) {
    return <TopPerformersSkeleton />;
  }

  // Get venues and service categories from the data using type guards
  const venuesCategories = dashboardData.filter(isVenueCard) as IVenueCard[];
  const serviceCategories = dashboardData.filter(
    isServiceCategoryCard,
  ) as IServiceCategoryCard[];
  const foodMenuesCategories = dashboardData.filter(isFoodCard) as IFoodCard[];

  // Get top venues by lead count
  const topVenues = venuesCategories
    .map((venue) => {
      const venueInfo = venue.leads?.[0]?.upselling_venue;
      return {
        venue_uuid: venue.venue_uuid,
        venue_name: venue.venue_name,
        leadCount: venue.leads?.length || 0,
        displayCapacity:
          venueInfo?.capacity || venueInfo?.floating_capacity || null,
        displayStatus: venueInfo?.status || "ACTIVE",
      };
    })
    .sort((a, b) => b.leadCount - a.leadCount)
    .slice(0, 5);

  // Get top service categories by lead count
  const topServiceCategories = serviceCategories
    .map((category) => {
      const firstService = category.leads?.[0]?.upselling_services?.[0];
      return {
        service_category_uuid: category.service_category_uuid,
        service_category_name: category.service_category_name,
        leadCount: category.leads?.length || 0,
        displayStatus: firstService?.status || "ACTIVE",
      };
    })
    .sort((a, b) => b.leadCount - a.leadCount)
    .slice(0, 5);

  // Get top food menu categories by lead count
  const topFoodCategories = foodMenuesCategories
    .map((food) => {
      const foodInfo = food.leads?.[0]?.upselling_food_item;
      return {
        food_item_uuid: food.food_item_uuid,
        item_name: food.item_name,
        leadCount: food.leads?.length || 0,
        displayStatus: foodInfo?.status || "ACTIVE",
      };
    })
    .sort((a, b) => b.leadCount - a.leadCount)
    .slice(0, 5);

  return (
    <Grid container spacing={{ xs: 2, md: 3 }}>
      {/* Top Venues */}
      <Grid item xs={12} md={4}>
        <StandardCard
          sx={{
            height: { xs: "auto", md: "500px" },
            minHeight: { xs: "auto", md: "400px" },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, md: 3 },
              "&:last-child": { pb: { xs: 2, md: 3 } },
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                fontSize: { xs: "1.05rem", md: "1.25rem" },
              }}
            >
              <StorefrontIcon
                sx={{
                  mr: 1,
                  color: "primary.main",
                  fontSize: { xs: 20, md: 24 },
                }}
              />
              Top 5 Performing Venues
            </Typography>

            {topVenues?.length > 0 ? (
              <List disablePadding>
                {topVenues?.map((venue) => (
                  <ListItem key={venue.venue_uuid} sx={{ px: 0, py: 0.75 }}>
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 34,
                          height: 34,
                          fontSize: "0.85rem",
                          fontWeight: 700,
                        }}
                      >
                        {venue.venue_name?.charAt(0) || "V"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={venue.venue_name}
                      secondary={`Capacity: ${venue.displayCapacity || "N/A"} | ${
                        venue.leadCount
                      } leads`}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.88rem",
                        noWrap: true,
                        sx: { pr: 1 },
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        noWrap: true,
                      }}
                    />
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <CustomChip
                        color={
                          venue.displayStatus === "ACTIVE"
                            ? "success"
                            : "default"
                        }
                        content={venue.displayStatus}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <NoRecordFound />
            )}
          </CardContent>
        </StandardCard>
      </Grid>

      {/* Top Services */}
      <Grid item xs={12} md={4}>
        <StandardCard
          sx={{
            height: { xs: "auto", md: "500px" },
            minHeight: { xs: "auto", md: "400px" },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, md: 3 },
              "&:last-child": { pb: { xs: 2, md: 3 } },
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                fontSize: { xs: "1.05rem", md: "1.25rem" },
              }}
            >
              <RoomServiceIcon
                sx={{
                  mr: 1,
                  color: "primary.main",
                  fontSize: { xs: 20, md: 24 },
                }}
              />
              Top 5 Performing Services
            </Typography>

            {topServiceCategories?.length > 0 ? (
              <List disablePadding>
                {topServiceCategories?.map((category) => (
                  <ListItem
                    key={category.service_category_uuid}
                    sx={{ px: 0, py: 0.75 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 34,
                          height: 34,
                          fontSize: "0.85rem",
                          fontWeight: 700,
                        }}
                      >
                        {category.service_category_name?.charAt(0) || "S"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={category.service_category_name}
                      secondary={`${category.leadCount} leads | ${category.displayStatus}`}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.88rem",
                        noWrap: true,
                        sx: { pr: 1 },
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        noWrap: true,
                      }}
                    />
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <CustomChip
                        color={
                          category.displayStatus === "ACTIVE"
                            ? "success"
                            : "default"
                        }
                        content={category.displayStatus}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <NoRecordFound />
            )}
          </CardContent>
        </StandardCard>
      </Grid>

      {/* Top Food */}
      <Grid item xs={12} md={4}>
        <StandardCard
          sx={{
            height: { xs: "auto", md: "500px" },
            minHeight: { xs: "auto", md: "400px" },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, md: 3 },
              "&:last-child": { pb: { xs: 2, md: 3 } },
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                fontSize: { xs: "1.05rem", md: "1.25rem" },
              }}
            >
              <FastfoodIcon
                sx={{
                  mr: 1,
                  color: "primary.main",
                  fontSize: { xs: 20, md: 24 },
                }}
              />
              Top 5 Performing Food Menus
            </Typography>
            {topFoodCategories?.length > 0 ? (
              <List disablePadding>
                {topFoodCategories?.map((category) => (
                  <ListItem
                    key={category.food_item_uuid}
                    sx={{ px: 0, py: 0.75 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 34,
                          height: 34,
                          fontSize: "0.85rem",
                          fontWeight: 700,
                        }}
                      >
                        {category.item_name?.charAt(0) || "F"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={category.item_name}
                      secondary={`${category.leadCount} leads | ${category.displayStatus}`}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.88rem",
                        noWrap: true,
                        sx: { pr: 1 },
                      }}
                      secondaryTypographyProps={{
                        fontSize: "0.75rem",
                        noWrap: true,
                      }}
                    />
                    <ListItemSecondaryAction sx={{ right: 0 }}>
                      <CustomChip
                        color={
                          category.displayStatus === "ACTIVE"
                            ? "success"
                            : "default"
                        }
                        content={category.displayStatus}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <NoRecordFound />
            )}
          </CardContent>
        </StandardCard>
      </Grid>
    </Grid>
  );
};
